import { Collapse, LinearProgress, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface IUseFormikUtilsProps {
  errorsOnlyIfTouched?: boolean
  errorsOnlyIfDirty?: boolean
  useAnimations?: boolean
}
const defaultProps: IUseFormikUtilsProps = {
  errorsOnlyIfTouched: true,
  errorsOnlyIfDirty: false,
  useAnimations: true,
}
export const useFormikUtils = ({
  errorsOnlyIfTouched,
  errorsOnlyIfDirty,
  useAnimations,
}: IUseFormikUtilsProps = defaultProps) => {
  const formik = useFormikContext()
  const { t } = useTranslation()

  const getFieldMeta = useMemo(() => formik.getFieldMeta, [formik.getFieldMeta])
  const isDirty = useMemo(() => formik.dirty, [formik.dirty])

  const getInputErrorProps = useCallback(
    (
      fieldName: string,
      onlyIfTouched = errorsOnlyIfTouched,
      onlyIfDirty = errorsOnlyIfDirty,
      animationsEnabled = useAnimations,
    ) => {
      const fieldMeta = getFieldMeta(fieldName)
      let helperText = t(fieldMeta.error) as ReactNode
      if (onlyIfTouched && !fieldMeta.touched) {
        helperText = ''
      }
      if (onlyIfDirty && !isDirty) {
        helperText = ''
      }

      if (helperText !== '' && helperText !== undefined && animationsEnabled) {
        helperText = (
          <>
            <Collapse in={formik.isValidating}>
              <LinearProgress />
            </Collapse>
            <Collapse in={!formik.isValidating}>
              <Typography variant='caption'>{helperText}</Typography>
            </Collapse>
          </>
        )
      }

      return {
        helperText,
        error: Boolean(helperText),
      }
    },
    [
      errorsOnlyIfTouched,
      errorsOnlyIfDirty,
      useAnimations,
      isDirty,
      getFieldMeta,
      formik.isValidating,
      t,
    ],
  )

  return {
    getInputErrorProps,
  }
}
